import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { toast } from 'react-toastify';

interface Props {
    open: boolean;
    currentThumbnailUrl: string | null;
    onConfirm: (file: File) => Promise<void>;
    onClose: () => void;
}

const ThumbnailModal: React.FC<Props> = ({ open, currentThumbnailUrl, onConfirm, onClose }) => {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setFile(null);
            setPreview(null);
        }
    }, [open]);

    useEffect(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const pickFile = (fileList: FileList | null) => {
        const picked = fileList?.[0];
        if (!picked) return;
        if (!picked.type.startsWith('image/')) {
            toast.error('Vui lòng chọn một tệp ảnh');
            return;
        }
        setFile(picked);
    };

    const handleClose = () => {
        if (loading) return;
        onClose();
    };

    const handleOk = async () => {
        if (!file) return;
        setLoading(true);
        try {
            await onConfirm(file);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Đổi thumbnail"
            open={open}
            onOk={handleOk}
            onCancel={handleClose}
            okText="Lưu"
            cancelText="Hủy"
            confirmLoading={loading}
            okButtonProps={{ disabled: !file }}
            width={420}
        >
            <div
                className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 cursor-pointer transition-colors hover:border-primary/60"
                onClick={() => !loading && inputRef.current?.click()}
            >
                {preview || currentThumbnailUrl ? (
                    <img
                        src={preview ?? currentThumbnailUrl ?? undefined}
                        alt="Thumbnail"
                        className="aspect-3/4 w-40 rounded-md object-cover"
                    />
                ) : (
                    <p className="text-sm text-text-secondary">Chọn một ảnh để làm thumbnail. (Khuyến khích tỉ lệ 3:4)</p>
                )}
                <p className="text-xs text-text-muted">{file ? file.name : 'Bấm để chọn ảnh'}</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => pickFile(e.target.files)}
                />
            </div>
        </Modal>
    );
};

export default ThumbnailModal;
